import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useLocalStorage } from "@vueuse/core";

import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";
import type { AuthorizationsByShortCode, AuthorizedFeaturesApiResponse } from "@tmi/types/api/permissions";

import userPermissionsFactory, { type UserPermissions } from "@/types/permissions";

import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

// Global State
const localReferenceToPermissionsData = useLocalStorage< AuthorizationsByShortCode[] >( 'permissions', [] as unknown as AuthorizationsByShortCode[] );

const permissions = ref< UserPermissions >( userPermissionsFactory.create( localReferenceToPermissionsData.value ) );
const isInitialized = computed< boolean >( () => permissions.value.isInitialized() );
const loading = ref< boolean >( false );
const hasAccessToAnyTools = ref< boolean | null >( null );

export const usePermissionsStore = defineStore( 'permissions', () => {
    const { authToken } = storeToRefs( useAuthStore() );
    const userStore = useUserStore();

    function reset(): void {
        // Clear out the local storage value
        localReferenceToPermissionsData.value = null;

        hasAccessToAnyTools.value = false;
        loading.value = false;

        // Reset permissions state to the original value (empty)
        localReferenceToPermissionsData.value = [] as unknown as AuthorizationsByShortCode[];
        permissions.value = userPermissionsFactory.create( localReferenceToPermissionsData.value );
    }

    function authorizationGrants(): string {
        return JSON.stringify( localReferenceToPermissionsData.value );
    }

    async function initializeAsync(): Promise< void > {
        if ( isInitialized.value || loading.value ) {
            return;
        }

        loading.value = true;

        const result =
            await goFetch< AuthorizedFeaturesApiResponse, PersistedQueryPayload >( {
                endpoint: 'authorization/graphql',
                payload: {
                    id: '3b0bdec2f61d30769f20a7db357f00d0',
                    variables: { userId: userStore.userId()! }
                },
                authToken: authToken.value
            } );

        if ( null != result?.data?.authorizedFeatureGrants ) {
            // Load the overlay of all authorized features
            localReferenceToPermissionsData.value = [ ...( result.data.authorizedFeatureGrants.grants ) ];

            // Parse into a minimal standard set of permissions
            initializeSynchronously();

            // Set initialized based on if permissions were initialized
            if ( !isInitialized.value ) {
                // TODO: Handle the situation where initialization finished, but had some sort of error
                logger.error( 'Permissions were initialized, but an error exists within the logic/data' );
            }

            // Clear the "loading" flag
            loading.value = false;
        }
    }

    // This is necessary for the router to be able to navigate to any link without
    // initially assuming the user doesn't have access (403).
    //
    // This should only be called from the watcher during the initialization process
    // or from the router when the user is considered to be authenticated, but the
    // permissions have not yet been initialized
    function initializeSynchronously() {
        permissions.value = userPermissionsFactory.create( localReferenceToPermissionsData.value! );
    }

    function hasAccessBasedOnAnyShortCode( shortCodes: string[] ): boolean {
        let hasAccess = false;
        shortCodes.forEach( code => hasAccess = hasAccess || hasAccessBasedOnShortCode( code ) );

        return hasAccess;
    }

    function hasAccessBasedOnShortCode( shortCode: string ): boolean {
        if ( !( shortCode && isInitialized.value ) ) {
            return false;
        }

        return permissions.value.isAuthorizedByShortCode( shortCode );
    }

    function hasFeatureAccess( feature: string ): boolean {
        return permissions.value.hasFeatureAccess( feature );
    }

    function hasArticleAccessForProduct( product: string ): boolean {
        return permissions.value.hasArticleAccessForProduct( product );
    }

    function hasBonusReportAccessForProduct( product: string ): boolean {
        return permissions.value.hasBonusReportAccessForProduct( product );
    }

    function hasCourseAccessForProduct( product: string ): boolean {
        return permissions.value.hasCourseAccessForProduct( product );
    }

    function hasModelPortfolioPermissionsForProduct( productSlug: string ): boolean {
        return permissions.value.hasModelPortfolioAccess( productSlug );
    }

    function hasWebinarAccessForProduct( product: string ): boolean {
        return permissions.value.hasWebinarAccessForProduct( product );
    }

    /******************************************************************************************************************/
    //                                          GETTERS
    /******************************************************************************************************************/

    const accessibleModelPortfoliosSlugs = computed< string[] >( () => permissions.value.getModelPortfolioSlugsForAccessibleProducts() );

    /********************************************** FEATURE PERMISSIONS ***********************************************/
    const isAllianceMember = computed< boolean >(() => permissions.value.isAllianceMember() );

    const hasArticleAccess = computed< boolean >(() => permissions.value.hasArticleAccess() );
    const hasBonusReportAccess = computed< boolean >(() => permissions.value.hasBonusReportAccess() );
    const hasCourseAccess = computed< boolean >(() => permissions.value.hasCourseAccess() );
    const hasTradeOfTheDayAccess = computed< boolean >(() => permissions.value.hasTradeOfTheDayAccess() );
    const hasStockAccess = computed< boolean >(() => permissions.value.hasStockAccess() );
    const hasEtfAccess = computed< boolean >(() => permissions.value.hasEtfAccess() );
    const hasEtfListingAccess = computed< boolean >(() => permissions.value.hasEtfListingAccess() );
    const hasEquityCalculatorAccess = computed< boolean >(() => permissions.value.hasEquityCalculatorAccess() );
    const hasMainSearchAccess = computed< boolean >(() => permissions.value.hasMainSearchAccess() );
    const hasMajorIndexBreadthAccess = computed< boolean >(() => permissions.value.hasMajorIndexBreadthAccess() );
    const hasModelPortfolioAccess = computed< boolean >(() => permissions.value.hasAnyModelPortfolioAccess() );
    const hasNyseBpiAccess = computed< boolean >(() => permissions.value.hasNyseBpiAccess() );
    const hasOptionsCalculatorAccess = computed< boolean >(() => false );
    // const hasOptionsCalculatorAccess = computed< boolean >(() => permissions.value.hasOptionsCalculatorAccess() );
    const hasPortfolioBuilderAccess = computed< boolean >(() => permissions.value.hasPortfolioBuilderAccess() );
    const hasPositionSizeCalculatorAccess = computed< boolean >(() => permissions.value.hasPositionSizeCalculatorAccess() );
    const hasStockReplacementStrategyCalculatorAccess = computed< boolean >(() => permissions.value.hasStockReplacementStrategyCalculatorAccess() );
    const hasProductTradeAlertsAccess = computed< boolean >(() => permissions.value.hasProductTradeAlertsAccess() );
    const hasProfileAccess = computed< boolean >(() => permissions.value.hasProfileAccess() );
    const hasRelativeStrengthMatrixAccess = computed< boolean >(() => permissions.value.hasRelativeStrengthMatrixAccess() );
    const hasScannerAccess = computed< boolean >(() => permissions.value.hasScannerAccess() );

    const hasSectorAlertsAccess = computed< boolean >(() => permissions.value.hasSectorAlertsAccess() );

    const hasSectorAccess = computed< boolean >(() => permissions.value.hasSectorAccess() );

    const hasSectorBpiAccess = computed< boolean >(() => permissions.value.hasSectorBpiAccess() );
    const hasSectorBreadthAccess = computed< boolean >(() => permissions.value.hasSectorBreadthAccess() );
    const hasSpreadCalculatorAccess = computed< boolean >(() => permissions.value.hasSpreadCalculatorAccess() );

    // Build the composite value from any of the criteria that would allow for the Tools menu to be displayed
    const hasToolsAccess = computed< boolean >( () =>
        // Portfolio Tools
        hasPortfolioBuilderAccess.value ||
        hasEquityCalculatorAccess.value ||
        hasOptionsCalculatorAccess.value ||
        hasPositionSizeCalculatorAccess.value ||
        hasStockReplacementStrategyCalculatorAccess.value ||
        hasSpreadCalculatorAccess.value ||
        // Indicators
        hasSectorBpiAccess.value ||
        hasNyseBpiAccess.value ||
        hasRelativeStrengthMatrixAccess.value ||
        hasMajorIndexBreadthAccess.value ||
        // Reference Tools
        hasEtfListingAccess.value ||
        // First to short-circuit the need to test others (Sector Tree & Sectors)
        hasSectorAccess.value );

    const hasWebinarAccess = computed< boolean >( () =>  permissions.value.hasWebinarAccess() );

    // Legacy???
    const hasMyLibraryAccess = hasArticleAccess || hasWebinarAccess || hasBonusReportAccess;

    return {
        // STATE
        isInitialized,

        // ACTIONS
        authorizationGrants,
        initializeAsync,
        initializeSynchronously,
        hasAccessBasedOnShortCode,
        hasAccessBasedOnAnyShortCode,
        hasArticleAccessForProduct,
        hasBonusReportAccessForProduct,
        hasCourseAccessForProduct,
        hasFeatureAccess,
        hasModelPortfolioPermissionsForProduct,
        hasWebinarAccessForProduct,
        reset,

        // GETTERS
        accessibleModelPortfoliosSlugs,
        hasArticleAccess,
        hasBonusReportAccess,
        hasCourseAccess,
        hasEtfAccess,
        hasStockAccess,
        hasEtfListingAccess,
        hasEquityCalculatorAccess,
        hasMainSearchAccess,
        hasMajorIndexBreadthAccess,
        hasModelPortfolioAccess,
        hasNyseBpiAccess,
        hasOptionsCalculatorAccess,
        hasPortfolioBuilderAccess,
        hasPositionSizeCalculatorAccess,
        hasStockReplacementStrategyCalculatorAccess,
        hasProductTradeAlertsAccess,
        hasProfileAccess,
        hasRelativeStrengthMatrixAccess,
        hasScannerAccess,
        hasSectorAlertsAccess,
        hasSectorAccess,
        hasSectorBpiAccess,
        hasSectorBreadthAccess,
        hasSpreadCalculatorAccess,
        hasTradeOfTheDayAccess,
        hasToolsAccess,
        hasWebinarAccess,
        hasMyLibraryAccess,
        isAllianceMember
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( usePermissionsStore, import.meta.hot ) );
}
